import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, Mail, Phone, MapPin, Calendar, MessageSquare } from "lucide-react";
import { admin } from "../api";

export default function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await admin.get("/admin/submissions");
      setItems(data);
      if (data.length && (selected === null || !data.find((s) => s.id === selected?.id))) {
        setSelected(data[0]);
      }
      if (!data.length) setSelected(null);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function remove(id) {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await admin.delete(`/admin/submissions/${id}`);
      toast.success("Submission deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  }

  if (loading) return <div className="font-body text-[13px] text-[#FDFBF7]/70">Loading…</div>;

  if (items.length === 0) {
    return (
      <div className="border border-dashed border-[#D4AF37]/25 py-24 text-center">
        <div className="font-italic-serif text-2xl text-[#D4AF37]/80">No enquiries yet.</div>
        <div className="mt-2 font-body text-[13px] text-[#FDFBF7]/50">
          When someone submits the contact form, it will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[360px_1fr]">
      <ul className="space-y-1 border border-[#D4AF37]/25 max-h-[70vh] overflow-y-auto">
        {items.map((s) => {
          const active = selected?.id === s.id;
          return (
            <li key={s.id}>
              <button
                onClick={() => setSelected(s)}
                data-testid={`admin-sub-${s.id}`}
                className={`w-full border-l-2 px-5 py-4 text-left transition-colors ${
                  active
                    ? "border-l-[#D4AF37] bg-[#1A050B]"
                    : "border-l-transparent hover:bg-[#1A050B]/50"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-serif text-lg text-[#FDFBF7]">{s.name}</div>
                  <div className="font-body text-[10px] uppercase tracking-[0.18em] text-[#FDFBF7]/50">
                    {new Date(s.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
                <div className="mt-1 font-body text-[12px] text-[#D4AF37]">{s.event_type}</div>
                <div className="mt-1 line-clamp-1 font-body text-[12px] text-[#FDFBF7]/60">
                  {s.message}
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {selected && (
        <motion.div
          key={selected.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="border border-[#D4AF37]/25 p-8 md:p-10"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-body text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                {selected.event_type}
              </div>
              <div className="mt-2 font-serif text-4xl leading-none">{selected.name}</div>
              <div className="mt-2 font-body text-[12px] text-[#FDFBF7]/50">
                {new Date(selected.created_at).toLocaleString()} · id {selected.id}
              </div>
            </div>
            <button
              onClick={() => remove(selected.id)}
              data-testid="admin-sub-delete"
              className="pill pill-ghost text-[#F5D97A]/80 hover:text-[#F5D97A]"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Info icon={Mail} label="Email">
              <a href={`mailto:${selected.email}`} className="hover:text-[#D4AF37] transition-colors">{selected.email}</a>
            </Info>
            <Info icon={Phone} label="Phone">
              <a href={`tel:${selected.phone}`} className="hover:text-[#D4AF37] transition-colors">{selected.phone}</a>
            </Info>
            {selected.event_date && (
              <Info icon={Calendar} label="Event date">{selected.event_date}</Info>
            )}
            <Info icon={MapPin} label="Event type">{selected.event_type}</Info>
          </div>

          <div className="mt-10 border-t border-[#D4AF37]/20 pt-6">
            <div className="flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
              <MessageSquare className="h-3.5 w-3.5" /> Message
            </div>
            <p className="mt-3 whitespace-pre-wrap font-body text-[15px] leading-[1.75] text-[#FDFBF7]">
              {selected.message}
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            <a
              href={`mailto:${selected.email}?subject=Re: your ${selected.event_type} enquiry`}
              className="pill pill-primary"
            >
              Reply by email
            </a>
            <a
              href={`https://wa.me/${selected.phone.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="pill pill-ghost"
            >
              WhatsApp
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]/80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-body text-[14px] text-[#FDFBF7]">{children}</div>
    </div>
  );
}
